import React, { useRef } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Slider } from '../../../components/ui/slider';
import { Sheet, SheetContent } from '../../../components/ui/sheet';
import { ArrowLeft } from 'lucide-react';
import { formatNumberWithCommas } from '../utils';
import { CommuteFilter } from './CommuteFilter';

// Constants for hybrid lot size slider
const SQFT_MAX = 10890; // 0.25 acres in sqft (43560 * 0.25)
const ACRES_MIN = 0.25;
const ACRES_MAX_MIN = 20; // for min slider
const ACRES_MAX_MAX = 25; // for max slider
const ACRES_STEP = 0.25;
const SLIDER_MAX = 1000;
const SLIDER_SPLIT = 500;

// Helper functions for hybrid slider
const sqftToAcres = (sqft: number): number => sqft / 43560;
const acresToSqft = (acres: number): number => acres * 43560;

function sliderToLotSize(val: number, isMax = false): number {
  if (val <= SLIDER_SPLIT) {
    // First half: linear scale for sqft (0 to SQFT_MAX)
    return Math.round((val / SLIDER_SPLIT) * SQFT_MAX);
  } else {
    // Second half: exponential scale for acres (ACRES_MIN to ACRES_MAX)
    const maxAcres = isMax ? ACRES_MAX_MAX : ACRES_MAX_MIN;
    const normalizedVal = (val - SLIDER_SPLIT) / (SLIDER_MAX - SLIDER_SPLIT);
    const acres = ACRES_MIN + (maxAcres - ACRES_MIN) * Math.pow(normalizedVal, 2);
    return Math.round(acresToSqft(acres));
  }
}

function lotSizeToSlider(val: number, isMax = false): number {
  const maxAcres = isMax ? ACRES_MAX_MAX : ACRES_MAX_MIN;
  const acres = sqftToAcres(val);
  
  if (acres <= ACRES_MIN) {
    // Convert to sqft scale
    return Math.round((val / SQFT_MAX) * SLIDER_SPLIT);
  } else {
    // Convert to acres scale
    const normalizedAcres = (acres - ACRES_MIN) / (maxAcres - ACRES_MIN);
    const sliderVal = SLIDER_SPLIT + Math.sqrt(normalizedAcres) * (SLIDER_MAX - SLIDER_SPLIT);
    return Math.round(sliderVal);
  }
}

import { useHomeFilters } from '../context';

export const FilterSheet: React.FC = () => {
  const {
    showFilters,
    setShowFilters,
    activeFilterTab,
    setActiveFilterTab,
    handleSetActiveFilterTab,
    isSearching,
    resetRentFilters,
    resetBuySoldFilters,
    setMinHomeAge,
    setMaxHomeAge,
    setMinSquareFeet,
    setMaxSquareFeet,
    setMinLotSize,
    setMaxLotSize,
    setMaxHoaFees,
    setNoHoa,
    setPendingDaysOnMarket,
    setFilters,
    setResetBouncing,
    resetBouncing,
    showSearchAreaDropdown,
    setShowSearchAreaDropdown,
    getCurrentSearchAreaMiles,
    getPendingSearchAreaMiles,
    getSearchAreaPillTouched,
    setPendingSearchAreaMilesForTab,
    setSearchAreaPillTouchedForTab,
    setCurrentSearchAreaMiles,
    setShowPropertyTypeModal,
    getPropertyTypePillTouched,
    getSelectedPropertyTypes,
    getCurrentPriceRange,
    setCurrentPriceRange,
    getCurrentBeds,
    setCurrentBeds,
    getCurrentBaths,
    setCurrentBaths,
    filters,
    handleFilterChange,
    showLotSizeDropdown,
    setShowLotSizeDropdown,
    minLotSize,
    maxLotSize,
    toggleFilterOption,
    setShowHomeAgeDropdown,
    minHomeAge,
    maxHomeAge,
    showHomeAgeDropdown,
    infoModal,
    setInfoModal,
    noHoa,
    maxHoaFees,
    setShowDaysOnMarketModal,
    setShowSquareFeetModal,
    getFilteredListingsCount,
    setCommuteAddressRent,
    setCommuteDriveTimeRent,
    setCommuteAddressTouched,
    setCommuteSuggestions,
    setCommuteCoords,
    setDriveTimes,
    setSearchLocationCoords,
    setSearchCity,
    setSearchState,
    minSquareFeet,
    maxSquareFeet,
    setShowSquareFeetDropdown,
    showSquareFeetDropdown,
  } = useHomeFilters();


  const searchAreaDropdownRef = useRef<HTMLDivElement>(null);
  const squareFeetDropdownRef = useRef<HTMLDivElement>(null);

  const isBuyOrSoldTab = (tab: string) => tab === 'buy' || tab === 'sold';
  const [hoaMode, setHoaMode] = React.useState<'any' | 'noHoa' | 'max'>('any');

  // Keep hoaMode in sync with maxHoaFees and noHoa
  React.useEffect(() => {
    if (noHoa) {
      setHoaMode('noHoa');
    } else if (maxHoaFees && maxHoaFees.trim() !== '') {
      setHoaMode('max');
    } else {
      setHoaMode('any');
    }
  }, [maxHoaFees, noHoa]);

  // Auto-scroll square feet dropdown into view when it opens (rent tab only)
  React.useEffect(() => {
    if (showSquareFeetDropdown && activeFilterTab === 'rent' && squareFeetDropdownRef.current) {
      // Small delay to ensure the dropdown is rendered
      setTimeout(() => {
        squareFeetDropdownRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }, 100);
    }
  }, [showSquareFeetDropdown, activeFilterTab]);

  return (
    <Sheet open={showFilters} onOpenChange={(open) => {
      if (!open) {
        // Close all secondary modals when the filters modal is closed
        setShowPropertyTypeModal(false);
        setShowSquareFeetModal(false);
        setShowDaysOnMarketModal(false);
        setShowSearchAreaDropdown(false);
        setShowLotSizeDropdown(false);
        setShowHomeAgeDropdown(false);
        setShowSquareFeetDropdown(false);
      }
      setShowFilters(open);
    }}>
      <SheetContent side="bottom" className="max-w-md mx-auto left-0 right-0 rounded-t-2xl p-0 overflow-hidden bg-background text-foreground border border-border shadow-xl">
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2 border-b border-border bg-background text-foreground relative rounded-t-2xl">
          <button
            onClick={() => {
              // Close all secondary modals when closing the filters modal
              setShowPropertyTypeModal(false);
              setShowSquareFeetModal(false);
              setShowDaysOnMarketModal(false);
              setShowSearchAreaDropdown(false);
              setShowLotSizeDropdown(false);
              setShowHomeAgeDropdown(false);
              setShowSquareFeetDropdown(false);
              setShowFilters(false);
            }}
            className="flex items-center justify-center bg-white/80 rounded-full p-2 shadow-xl hover:bg-blue-50 transition-colors"
            aria-label="Back"
            style={{ 
              height: '40px', 
              width: '40px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              zIndex: 10
            }}
          >
            <ArrowLeft className="h-6 w-6 text-blue-700" />
          </button>
          <span className="text-xl font-extrabold text-primary mx-auto">Filters</span>
          <button onClick={() => {
            if ((activeFilterTab as string) === 'rent') {
              resetRentFilters();
              setMinHomeAge('');
              setMaxHomeAge('');
            } else if (isBuyOrSoldTab(activeFilterTab)) {
              resetBuySoldFilters();
              setMinHomeAge('');
              setMaxHomeAge('');
            }
            setMinSquareFeet('');
            setMaxSquareFeet('');
            setMinLotSize('');
            setMaxLotSize('');
            setMaxHoaFees('');
            setNoHoa(false);
            setPendingDaysOnMarket(null);
              setFilters(prev => ({ 
              ...prev, 
              daysOnMarket: null, 
              hidePendingContingent: false,
              newConstructionOnly: false
            }));
            setResetBouncing(true);
            setTimeout(() => setResetBouncing(false), 400);
            // Reset commute filter state
            setCommuteAddressRent('');
            setCommuteDriveTimeRent(null);
            setCommuteAddressTouched(false);
            setCommuteSuggestions([]);
            setCommuteCoords(null);
            setDriveTimes(null);
            setSearchLocationCoords(null);
          }} className={`text-lg font-bold text-primary${resetBouncing ? ' animate-bounce' : ''}`}>Reset</button>
        </div>
        
        {/* Tabs */}
        <div className="flex items-center justify-between px-4 pt-2 pb-4 bg-background text-foreground">
          <button
            className={`flex-1 pb-2 font-bold ${activeFilterTab === 'buy' ? 'text-primary border-b-4 border-primary' : 'text-muted-foreground'}`}
            onClick={() => handleSetActiveFilterTab('buy')}
          >
            Buy
          </button>
          <button
            className={`flex-1 pb-2 font-bold ${activeFilterTab === 'rent' ? 'text-primary border-b-4 border-primary' : 'text-muted-foreground'}`}
            onClick={() => handleSetActiveFilterTab('rent')}
          >
            Rent
          </button>
          <button
            className={`flex-1 pb-2 font-bold ${activeFilterTab === 'sold' ? 'text-primary border-b-4 border-primary' : 'text-muted-foreground'}`}
            onClick={() => handleSetActiveFilterTab('sold')}
          >
            Just Sold
          </button>
        </div>
        
        {/* Filter sections */}
        <div className="px-4 pb-4 overflow-y-auto bg-background text-foreground h-[600px]" style={{ maxHeight: '60vh' }}>
          {/* Search area - now on all tabs */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-lg font-medium text-foreground">Search area</span>
              <button
                className="bg-primary text-white rounded-full px-5 py-2 font-bold text-base shadow hover:bg-blue-700 transition-colors"
                type="button"
                onClick={() => {
                  setShowSearchAreaDropdown(!showSearchAreaDropdown);
                }}
              >
                {getSearchAreaPillTouched() && getPendingSearchAreaMiles() !== null ? `${getPendingSearchAreaMiles()} miles` : 'Choose'}
              </button>
            </div>
            {showSearchAreaDropdown && (
              <div
                ref={searchAreaDropdownRef}
                className="mt-2 p-4 bg-muted rounded-xl border border-border flex flex-col items-center relative"
              >
                <button
                  className="absolute top-1 right-2 text-gray-700 hover:text-primary focus:outline-none"
                  type="button"
                  aria-label="Cancel search area change"
                  onClick={() => {
                    const pending = getPendingSearchAreaMiles() ?? 0;
                    const saved = getCurrentSearchAreaMiles();
                    if (pending !== saved) {
                      setPendingSearchAreaMilesForTab(saved);
                      setSearchAreaPillTouchedForTab(saved > 0);
                    }
                    setShowSearchAreaDropdown(false);
                  }}
                >
                  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
                <label className="block text-base font-medium mb-2">How far from your location do you want to search?</label>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={getPendingSearchAreaMiles() ?? 0}
                  onChange={e => setPendingSearchAreaMilesForTab(Number(e.target.value))}
                  className="w-full mb-2"
                />
                <div className="text-base font-bold mb-2">{getPendingSearchAreaMiles() ?? 0} miles</div>
                <div className="text-xs text-muted-foreground text-center mb-4">This controls how far from your chosen location the search will include properties. Increase the miles to see more results in a wider area.</div>
                <button
                  className="bg-primary text-white rounded-full px-5 py-2 font-bold text-base shadow hover:bg-blue-700 transition-colors w-full"
                  type="button"
                  onClick={() => {
                    const pending = getPendingSearchAreaMiles() ?? 0;
                    if (pending === 0) {
                      setSearchAreaPillTouchedForTab(false);
                      // Clear search location coords when search area is reset to 0
                      setSearchLocationCoords(null);
                      setSearchCity('');
                      setSearchState('');
                    } else {
                      setSearchAreaPillTouchedForTab(true);
                    }
                    setCurrentSearchAreaMiles(pending);
                    setShowSearchAreaDropdown(false);
                  }}
                >
                  Save
                </button>
              </div>
            )}
            <div className="text-sm text-muted-foreground">Available for city and zip code based search.</div>
          </div>
          {/* Property class - now on all tabs */}
          <div className="mb-4 flex items-center justify-between">
            <span className="text-lg font-medium text-foreground">Property class</span>
            <button
              className="bg-primary text-white rounded-full px-5 py-2 font-bold text-base shadow hover:bg-blue-700 transition-colors"
              onClick={() => setShowPropertyTypeModal(true)}
            >
              {!getPropertyTypePillTouched() || getSelectedPropertyTypes().length === 0
                ? 'Choose'
                : getSelectedPropertyTypes().length === 1
                  ? '1 selection'
                  : `${getSelectedPropertyTypes().length} selections`}
            </button>
          </div>
          {/* Tab-specific filter sections follow here... */}
          {(activeFilterTab as string) === 'rent' ? (
            <>
              {/* Monthly rent cost */}
              <div className="mb-4">
                <span className="text-lg font-medium text-foreground block mb-2">Monthly rent cost</span>
                <div className="flex flex-col gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Minimum rent cost</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9,]*"
                        className="!w-full rounded-xl border border-gray-300 !px-8 py-2 text-base"
                        placeholder="No minimum rent cost set"
                        value={formatNumberWithCommas(getCurrentPriceRange()[0] ?? '')}
                        onChange={e => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          const newRange: [number | null, number | null] = [val ? Number(val) : null, getCurrentPriceRange()[1]];
                          setCurrentPriceRange(newRange);
                        }}
                        min={0}
                      />
                    </div>
                    <div className="mt-2 mb-2">
                      <Slider
                        min={0}
                        max={10000}
                        step={100}
                        value={[getCurrentPriceRange()[0] ?? 0]}
                        onValueChange={([min]) => {
                          setCurrentPriceRange([
                            min === 0 ? null : min,
                            getCurrentPriceRange()[1],
                          ]);
                        }}
                      />
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>$0</span>
                        <span>$10,000</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Maximum rent cost</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9,]*"
                        className="!w-full rounded-xl border border-gray-300 !px-8 py-2 text-base"
                        placeholder="Enter maximum rent cost"
                        value={formatNumberWithCommas(getCurrentPriceRange()[1] ?? '')}
                        onChange={e => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          const newRange: [number | null, number | null] = [getCurrentPriceRange()[0], val ? Number(val) : null];
                          setCurrentPriceRange(newRange);
                        }}
                        min={0}
                      />
                    </div>
                    <div className="mt-2 mb-2">
                      <Slider
                        min={0}
                        max={10000}
                        step={100}
                        value={[getCurrentPriceRange()[1] ?? 10000]}
                        onValueChange={([max]) => {
                          setCurrentPriceRange([
                            getCurrentPriceRange()[0],
                            max === 10000 ? null : max,
                          ]);
                        }}
                      />
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>$0</span>
                        <span>$10,000</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Bedrooms filter */}
              <div className="mb-4">
                <span className="text-lg font-medium text-foreground block mb-2">Bedrooms</span>
                <div className="flex gap-2 flex-wrap pb-1">
                  {[null, 0, 1, 2, 3, 4, 5].map((val, idx) => (
                    <button
                      key={val === null ? 'any' : val === 0 ? 'studio' : val}
                      className={`rounded-full px-5 py-2 text-base font-bold border ${getCurrentBeds() === val ? 'bg-primary text-primary-foreground' : 'border-border text-foreground bg-background'}`}
                      onClick={() => setCurrentBeds(val)}
                      type="button"
                    >
                      {val === null ? 'Any' : val === 0 ? 'Studio+' : `${val}+`}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Bathrooms filter */}
              <div className="mb-4">
                <span className="text-lg font-medium text-foreground block mb-2">Bathrooms</span>
                <div className="flex gap-2 flex-wrap pb-1">
                  {[null, 1, 2, 3, 4, 5].map((val, idx) => (
                    <button
                      key={val === null ? 'any' : val}
                      className={`rounded-full px-5 py-2 text-base font-bold border ${getCurrentBaths() === val ? 'bg-primary text-primary-foreground' : 'border-border text-foreground bg-background'}`}
                      onClick={() => setCurrentBaths(val)}
                      type="button"
                    >
                      {val === null ? 'Any' : `${val}+`}
                    </button>
                  ))}
                </div>
              </div>
              
              <CommuteFilter />
              
              {/* Square Feet filter dropdown */}
              <div className="mb-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-lg font-medium text-foreground">Square feet</span>
                  <button
                    className="bg-primary text-white rounded-full px-5 py-2 font-bold text-base shadow hover:bg-blue-700 transition-colors"
                    type="button"
                    onClick={() => setShowSquareFeetDropdown(!showSquareFeetDropdown)}
                  >
                    {(() => {
                      const min = Number(minSquareFeet);
                      const max = Number(maxSquareFeet);
                      const minDisplay = min > 0 ? `${min} sqft` : 'No minimum';
                      const maxDisplay = max > 0 ? `${max} sqft` : 'No maximum';
                      if (!min && !max) return 'Choose';
                      return `${minDisplay} - ${maxDisplay}`;
                    })()}
                  </button>
                </div>
                {showSquareFeetDropdown && (
                  <div ref={squareFeetDropdownRef} className="mt-2 p-4 bg-muted rounded-xl border border-border flex flex-col items-center relative">
                    <button
                      className="absolute top-2 right-2 text-gray-700 hover:text-primary focus:outline-none"
                      type="button"
                      aria-label="Cancel square feet change"
                      onClick={() => setShowSquareFeetDropdown(false)}
                    >
                      <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                    <label className="block text-base font-medium mb-2 mt-2 w-full text-left">Square Feet</label>
                    <div className="w-full flex flex-col gap-4 mb-2">
                      <div className="mb-2 p-2 rounded-lg bg-gray-50 border border-gray-200">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm font-medium">Minimum</span>
                          <span className="text-xs text-muted-foreground">{minSquareFeet && Number(minSquareFeet) > 0 ? `${minSquareFeet} sqft` : 'No minimum'}</span>
                        </div>
                        <Slider
                          min={0}
                          max={7500}
                          step={100}
                          value={[Number(minSquareFeet) || 0]}
                          onValueChange={([val]) => setMinSquareFeet(val === 0 ? '' : String(val))}
                          className="h-4 slider-obvious"
                        />
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                          <span>0</span>
                          <span>7,500</span>
                        </div>
                      </div>
                      <div className="mb-2 p-2 rounded-lg bg-gray-50 border border-gray-200">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm font-medium">Maximum</span>
                          <span className="text-xs text-muted-foreground">{maxSquareFeet && Number(maxSquareFeet) > 0 ? `${maxSquareFeet} sqft` : 'No maximum'}</span>
                        </div>
                        <Slider
                          min={0}
                          max={10000}
                          step={100}
                          value={[Number(maxSquareFeet) || 0]}
                          onValueChange={([val]) => setMaxSquareFeet(val === 0 ? '' : String(val))}
                          className="h-4 slider-obvious"
                        />
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                          <span>0</span>
                          <span>10,000</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground text-center mb-4">Set a minimum and/or maximum square footage to filter properties by square footage.</div>
                    <button
                      className="bg-primary text-white rounded-full px-5 py-2 font-bold text-base shadow hover:bg-blue-700 transition-colors w-full"
                      onClick={() => setShowSquareFeetDropdown(false)}
                    >
                      Save
                    </button>
                  </div>
                )}
              </div>
              
              {/* Lot size dropdown */}
              {activeFilterTab !== 'rent' && (
                <div className="mb-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-lg font-medium text-foreground">Lot size</span>
                    <button
                      className="bg-primary text-white rounded-full px-5 py-2 font-bold text-base shadow hover:bg-blue-700 transition-colors"
                      type="button"
                      onClick={() => setShowLotSizeDropdown(!showLotSizeDropdown)}
                    >
                      {(() => {
                        const min = Number(minLotSize);
                        const max = Number(maxLotSize);
                        const minDisplay =
                          min > 0
                            ? min <= SQFT_MAX
                              ? `${min} sqft`
                              : `${(min / 43560).toFixed(2)} acres`
                            : 'No minimum';
                        const maxDisplay =
                          max > 0
                            ? max <= SQFT_MAX
                              ? `${max} sqft`
                              : `${(max / 43560).toFixed(2)} acres`
                            : 'No maximum';
                        if (!min && !max) return 'Choose';
                        return `${minDisplay} - ${maxDisplay}`;
                      })()}
                    </button>
                  </div>
                  {showLotSizeDropdown && (
                    <div className="mt-2 p-4 bg-muted rounded-xl border border-border flex flex-col items-center relative">
                      <button
                        className="absolute top-2 right-2 text-gray-700 hover:text-primary focus:outline-none"
                        type="button"
                        aria-label="Cancel lot size change"
                        onClick={() => setShowLotSizeDropdown(false)}
                      >
                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                      <label className="block text-base font-medium mb-2 mt-2 w-full text-left">Lot Size</label>
                      <div className="w-full flex flex-col gap-4 mb-2">
                        <div className="mb-2 p-2 rounded-lg bg-gray-50 border border-gray-200">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-medium">Minimum</span>
                            <span className="text-xs text-muted-foreground">{minLotSize && Number(minLotSize) > 0 ? (Number(minLotSize) <= SQFT_MAX ? `${minLotSize} sqft` : `${(Number(minLotSize)/43560).toFixed(2)} acres`) : 'No minimum'}</span>
                          </div>
                          <Slider
                            min={0}
                            max={SLIDER_MAX}
                            step={1}
                            value={[lotSizeToSlider(Number(minLotSize) || 0, false)]}
                            onValueChange={([sliderVal]) => {
                              const sqft = sliderToLotSize(sliderVal, false);
                              setMinLotSize(String(sqft));
                            }}
                            className="h-4 slider-obvious"
                          />
                          <div className="flex justify-between text-xs text-muted-foreground mt-1">
                            <span>0 sqft</span>
                            <span>20 acres</span>
                          </div>
                        </div>
                        <div className="mb-2 p-2 rounded-lg bg-gray-50 border border-gray-200">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-medium">Maximum</span>
                            <span className="text-xs text-muted-foreground">{maxLotSize && Number(maxLotSize) > 0 ? (Number(maxLotSize) <= SQFT_MAX ? `${maxLotSize} sqft` : `${(Number(maxLotSize)/43560).toFixed(2)} acres`) : 'No maximum'}</span>
                          </div>
                          <Slider
                            min={0}
                            max={SLIDER_MAX}
                            step={1}
                            value={[lotSizeToSlider(Number(maxLotSize) || 0, true)]}
                            onValueChange={([sliderVal]) => {
                              const sqft = sliderToLotSize(sliderVal, true);
                              setMaxLotSize(String(sqft));
                            }}
                            className="h-4 slider-obvious"
                          />
                          <div className="flex justify-between text-xs text-muted-foreground mt-1">
                            <span>0 sqft</span>
                            <span>25 acres</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground text-center mb-4">Set a minimum and/or maximum lot size to filter properties by lot size.</div>
                      <button
                        className="bg-primary text-white rounded-full px-5 py-2 font-bold text-base shadow hover:bg-blue-700 transition-colors w-full"
                        onClick={() => setShowLotSizeDropdown(false)}
                      >
                        Save
                      </button>
                    </div>
                  )}
                </div>
              )}
              
            </>
          ) : (
            // Begin exact copy of Buy/Sold filter sections from Home.tsx
            <div>
              <div className="mb-4">
                <span className="text-lg font-medium text-foreground block mb-2">Price</span>
                  <>
                    <div className="flex flex-col gap-2 mb-2">
                      {/* Minimum price */}
                      <div>
                        <label className="block text-sm font-medium mb-1">Minimum price</label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                          <Input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9,]*"
                            className="!w-full rounded-xl border border-gray-300 !px-8 py-2 text-base"
                            placeholder="No minimum price set"
                            value={formatNumberWithCommas(getCurrentPriceRange()[0] ?? '')}
                            onChange={e => {
                              const val = e.target.value.replace(/[^0-9]/g, '');
                              setCurrentPriceRange([val ? Number(val) : null, getCurrentPriceRange()[1]]);
                            }}
                            min={0}
                          />
                        </div>
                        <div className="px-2 mt-2 mb-2">
                          <Slider
                            min={0}
                            max={15000000}
                            step={10000}
                            value={[getCurrentPriceRange()[0] ?? 0]}
                            onValueChange={([min]) => {
                              setCurrentPriceRange([
                                min === 0 ? null : min,
                                getCurrentPriceRange()[1],
                              ]);
                            }}
                          />
                          <div className="flex justify-between text-xs text-muted-foreground mt-1">
                            <span>$0</span>
                            <span>$15,000,000</span>
                          </div>
                        </div>
                      </div>
                      {/* Maximum price */}
                      <div>
                        <label className="block text-sm font-medium mb-1">Maximum price</label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                          <Input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9,]*"
                            className="!w-full rounded-xl border border-gray-300 !px-8 py-2 text-base"
                            placeholder="No maximum price set"
                            value={formatNumberWithCommas(getCurrentPriceRange()[1] ?? '')}
                            onChange={e => {
                              const val = e.target.value.replace(/[^0-9]/g, '');
                              setCurrentPriceRange([getCurrentPriceRange()[0], val ? Number(val) : null]);
                            }}
                            min={0}
                          />
                        </div>
                        <div className="px-2 mt-2 mb-2">
                          <Slider
                            min={0}
                            max={15000000}
                            step={10000}
                            value={[getCurrentPriceRange()[1] ?? 15000000]}
                            onValueChange={([max]) => {
                              setCurrentPriceRange([
                                getCurrentPriceRange()[0],
                                max === 15000000 ? null : max,
                              ]);
                            }}
                          />
                          <div className="flex justify-between text-xs text-muted-foreground mt-1">
                            <span>$0</span>
                            <span>{`$${(() => {
                  const maxPrice = getCurrentPriceRange()[1];
                  return typeof maxPrice === 'number' ? maxPrice.toLocaleString() : '15,000,000';
                })()}`}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Removed the combined min/max range slider */}
                  </>
              </div>
              {/* Bedrooms filter */}
              <div className="mb-4">
                <span className="text-lg font-medium text-foreground block mb-2">Bedrooms</span>
                <div className="flex gap-2 flex-wrap pb-1">
                  {[null, 0, 1, 2, 3, 4, 5].map((val, idx) => (
                    <button
                      key={val === null ? 'any' : val === 0 ? 'studio' : val}
                      className={`rounded-full px-5 py-2 text-base font-bold border ${getCurrentBeds() === val ? 'bg-primary text-primary-foreground' : 'border-border text-foreground bg-background'}`}
                      onClick={() => setCurrentBeds(val)}
                      type="button"
                    >
                      {val === null ? 'Any' : val === 0 ? 'Studio+' : `${val}+`}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Bathrooms filter */}
              <div className="mb-4">
                <span className="text-lg font-medium text-foreground block mb-2">Bathrooms</span>
                <div className="flex gap-2 flex-wrap pb-1">
                  {[null, 1, 2, 3, 4, 5].map((val, idx) => (
                    <button
                      key={val === null ? 'any' : val}
                      className={`rounded-full px-5 py-2 text-base font-bold border ${getCurrentBaths() === val ? 'bg-primary text-primary-foreground' : 'border-border text-foreground bg-background'}`}
                      onClick={() => setCurrentBaths(val)}
                      type="button"
                    >
                      {val === null ? 'Any' : `${val}+`}
                    </button>
                  ))}
                </div>
              </div>
              
              
              <CommuteFilter />
              
              {/* Lot Size, Pet Filters, Home Age, Toggles, HOA, etc. (copied exactly) */}
              {/* Square Feet filter dropdown */}
              <div className="flex items-center justify-between mb-1">
                <span className="text-lg font-medium text-foreground">Square feet</span>
                <button
                  className="bg-primary text-white rounded-full px-5 py-2 font-bold text-base shadow hover:bg-blue-700 transition-colors"
                  type="button"
                  onClick={() => setShowSquareFeetDropdown(!showSquareFeetDropdown)}
                >
                  {(() => {
                    const min = Number(minSquareFeet);
                    const max = Number(maxSquareFeet);
                    const minDisplay = min > 0 ? `${min} sqft` : 'No minimum';
                    const maxDisplay = max > 0 ? `${max} sqft` : 'No maximum';
                    if (!min && !max) return 'Choose';
                    return `${minDisplay} - ${maxDisplay}`;
                  })()}
                </button>
              </div>
              {showSquareFeetDropdown && (
                <div className="mt-2 p-4 bg-muted rounded-xl border border-border flex flex-col items-center relative">
                  <button
                    className="absolute top-2 right-2 text-gray-700 hover:text-primary focus:outline-none"
                    type="button"
                    aria-label="Cancel square feet change"
                    onClick={() => setShowSquareFeetDropdown(false)}
                  >
                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                  <label className="block text-base font-medium mb-2 mt-2 w-full text-left">Square Feet</label>
                  <div className="w-full flex flex-col gap-4 mb-2">
                    <div className="mb-2 p-2 rounded-lg bg-gray-50 border border-gray-200">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium">Minimum</span>
                        <span className="text-xs text-muted-foreground">{minSquareFeet && Number(minSquareFeet) > 0 ? `${minSquareFeet} sqft` : 'No minimum'}</span>
                      </div>
                      <Slider
                        min={0}
                        max={7500}
                        step={100}
                        value={[Number(minSquareFeet) || 0]}
                        onValueChange={([val]) => setMinSquareFeet(val === 0 ? '' : String(val))}
                        className="h-4 slider-obvious"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>0</span>
                        <span>7,500</span>
                      </div>
                    </div>
                    <div className="mb-2 p-2 rounded-lg bg-gray-50 border border-gray-200">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium">Maximum</span>
                        <span className="text-xs text-muted-foreground">{maxSquareFeet && Number(maxSquareFeet) > 0 ? `${maxSquareFeet} sqft` : 'No maximum'}</span>
                      </div>
                      <Slider
                        min={0}
                        max={10000}
                        step={100}
                        value={[Number(maxSquareFeet) || 0]}
                        onValueChange={([val]) => setMaxSquareFeet(val === 0 ? '' : String(val))}
                        className="h-4 slider-obvious"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>0</span>
                        <span>10,000</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground text-center mb-4">Set a minimum and/or maximum square footage to filter properties by square footage.</div>
                  <button
                    className="bg-primary text-white rounded-full px-5 py-2 font-bold text-base shadow hover:bg-blue-700 transition-colors w-full"
                    onClick={() => setShowSquareFeetDropdown(false)}
                  >
                    Save
                  </button>
                </div>
              )}
               {/* Lot size dropdown */}
              <div className="flex items-center justify-between mb-1">
                <span className="text-lg font-medium text-foreground">Lot size</span>
                <button
                  className="bg-primary text-white rounded-full px-5 py-2 font-bold text-base shadow hover:bg-blue-700 transition-colors"
                  type="button"
                  onClick={() => setShowLotSizeDropdown(!showLotSizeDropdown)}
                >
                  {(() => {
                    const min = Number(minLotSize);
                    const max = Number(maxLotSize);
                    const minDisplay =
                      min > 0
                        ? min <= SQFT_MAX
                          ? `${min} sqft`
                          : `${(min / 43560).toFixed(2)} acres`
                        : 'No minimum';
                    const maxDisplay =
                      max > 0
                        ? max <= SQFT_MAX
                          ? `${max} sqft`
                          : `${(max / 43560).toFixed(2)} acres`
                        : 'No maximum';
                    if (!min && !max) return 'Choose';
                    return `${minDisplay} - ${maxDisplay}`;
                  })()}
                </button>
              </div>
              {showLotSizeDropdown && (
                <div className="mt-2 p-4 bg-muted rounded-xl border border-border flex flex-col items-center relative">
                  <button
                    className="absolute top-2 right-2 text-gray-700 hover:text-primary focus:outline-none"
                    type="button"
                    aria-label="Cancel lot size change"
                    onClick={() => setShowLotSizeDropdown(false)}
                  >
                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                  <label className="block text-base font-medium mb-2 mt-2 w-full text-left">Lot Size</label>
                  <div className="w-full flex flex-col gap-4 mb-2">
                    <div className="mb-2 p-2 rounded-lg bg-gray-50 border border-gray-200">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium">Minimum</span>
                        <span className="text-xs text-muted-foreground">{minLotSize && Number(minLotSize) > 0 ? (Number(minLotSize) <= SQFT_MAX ? `${minLotSize} sqft` : `${(Number(minLotSize)/43560).toFixed(2)} acres`) : 'No minimum'}</span>
                      </div>
                      <Slider
                        min={0}
                        max={SLIDER_MAX}
                        step={1}
                        value={[lotSizeToSlider(Number(minLotSize) || 0, false)]}
                        onValueChange={([sliderVal]) => {
                          const sqft = sliderToLotSize(sliderVal, false);
                          setMinLotSize(String(sqft));
                        }}
                        className="h-4 slider-obvious"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>0 sqft</span>
                        <span>20 acres</span>
                      </div>
                    </div>
                    <div className="mb-2 p-2 rounded-lg bg-gray-50 border border-gray-200">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium">Maximum</span>
                        <span className="text-xs text-muted-foreground">{maxLotSize && Number(maxLotSize) > 0 ? (Number(maxLotSize) <= SQFT_MAX ? `${maxLotSize} sqft` : `${(Number(maxLotSize)/43560).toFixed(2)} acres`) : 'No maximum'}</span>
                      </div>
                      <Slider
                        min={0}
                        max={SLIDER_MAX}
                        step={1}
                        value={[lotSizeToSlider(Number(maxLotSize) || 0, true)]}
                        onValueChange={([sliderVal]) => {
                          const sqft = sliderToLotSize(sliderVal, true);
                          setMaxLotSize(String(sqft));
                        }}
                        className="h-4 slider-obvious"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>0 sqft</span>
                        <span>25 acres</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground text-center mb-4">Set a minimum and/or maximum lot size to filter properties by lot size.</div>
                  <button
                    className="bg-primary text-white rounded-full px-5 py-2 font-bold text-base shadow hover:bg-blue-700 transition-colors w-full"
                    onClick={() => setShowLotSizeDropdown(false)}
                  >
                    Save
                  </button>
                </div>
              )}
               {/* Pet Filters, Home Age, Toggles, HOA, etc. (copied exactly) */}
              
               {/* Home age for buy/sold tabs */}
               {activeFilterTab !== 'rent' && (
                 <>
                   <div className="flex items-center justify-between relative">
                     <span className="text-lg font-medium text-foreground">Home age</span>
                     <button 
                       className="bg-primary text-white rounded-full px-5 py-2 font-bold text-base shadow hover:bg-blue-700 transition-colors"
                       onClick={() => setShowHomeAgeDropdown(!showHomeAgeDropdown)}
                     >
                       {(!minHomeAge && !maxHomeAge) ? 'Choose' : `${minHomeAge ? minHomeAge + 'y' : 'No min'} - ${maxHomeAge ? maxHomeAge + 'y' : 'No max'}`}
                     </button>
                   </div>
                   {showHomeAgeDropdown && (
                     <div className="mt-2 p-4 bg-muted rounded-xl border border-border flex flex-col items-center relative">
                       <button
                         className="absolute top-2 right-2 text-gray-700 hover:text-primary focus:outline-none"
                         type="button"
                         aria-label="Cancel home age change"
                         onClick={() => setShowHomeAgeDropdown(false)}
                       >
                         <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                       </button>
                       <label className="block text-base font-medium mb-2 mt-2 w-full text-left">Home Age</label>
                       <div className="w-full flex flex-col gap-4 mb-2">
                         <div className="mb-2 p-2 rounded-lg bg-gray-50 border border-gray-200">
                           <div className="flex justify-between items-center mb-1">
                             <span className="text-sm font-medium">Minimum</span>
                             <span className="text-xs text-muted-foreground">{minHomeAge && Number(minHomeAge) > 0 ? `${minHomeAge} years` : 'No minimum'}</span>
                           </div>
                           <Slider
                             min={0}
                             max={100}
                             step={1}
                             value={[Number(minHomeAge) || 0]}
                             onValueChange={([val]) => setMinHomeAge(val === 0 ? '' : String(val))}
                             className="h-4 slider-obvious"
                           />
                           <div className="flex justify-between text-xs text-muted-foreground mt-1">
                             <span>0</span>
                             <span>100</span>
                           </div>
                         </div>
                         <div className="mb-2 p-2 rounded-lg bg-gray-50 border border-gray-200">
                           <div className="flex justify-between items-center mb-1">
                             <span className="text-sm font-medium">Maximum</span>
                             <span className="text-xs text-muted-foreground">{maxHomeAge && Number(maxHomeAge) > 0 ? `${maxHomeAge} years` : 'No maximum'}</span>
                           </div>
                           <Slider
                             min={0}
                             max={150}
                             step={1}
                             value={[Number(maxHomeAge) || 0]}
                             onValueChange={([val]) => setMaxHomeAge(val === 0 ? '' : String(val))}
                             className="h-4 slider-obvious"
                           />
                           <div className="flex justify-between text-xs text-muted-foreground mt-1">
                             <span>0</span>
                             <span>150</span>
                           </div>
                         </div>
                       </div>
                       <div className="text-xs text-muted-foreground text-center mb-4">Set a minimum and/or maximum home age to filter properties by year built.</div>
                       <button
                         className="bg-primary text-white rounded-full px-5 py-2 font-bold text-base shadow hover:bg-blue-700 transition-colors w-full"
                         onClick={() => setShowHomeAgeDropdown(false)}
                       >
                         Save
                       </button>
                     </div>
                   )}
                 </>
               )}
               {/* Toggles and HOA (copied exactly) */}
               {/* Toggles */}
               <div className="mb-4 flex flex-col gap-2">
                 
               

                 {/* Hide pending / contingent */}
                 {activeFilterTab !== 'rent' && activeFilterTab !== 'sold' && (
                   <div className="flex items-center justify-between relative">
                     <span className="text-lg font-medium text-foreground flex items-center gap-1">Hide pending / contingent
                       <button
                         type="button"
                         className="ml-1 text-gray-400 hover:text-primary focus:outline-none"
                         onClick={() => setInfoModal('pending')}
                         aria-label="Info about pending/contingent"
                       >
                         <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="1"/></svg>
                       </button>
                     </span>
                     <button className="flex items-center justify-between py-3" onClick={() => toggleFilterOption('hidePendingContingent')}>
                       <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${filters.hidePendingContingent ? 'border-primary bg-primary' : 'border-gray-300 bg-white'}`}>
                         {filters.hidePendingContingent && (
                           <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg>
                         )}
                       </span>
                     </button>
                     {/* Info Modal for pending/contingent */}
                     {infoModal === 'pending' && (
                       <div className="absolute left-1/2 top-full mt-2 -translate-x-1/2 z-50 bg-background text-foreground border border-border rounded-xl shadow-lg p-4 w-64 text-sm">
                         <div className="flex justify-between items-center mb-2">
                           <span className="font-bold">Pending / Contingent</span>
                           <button onClick={() => setInfoModal(null)} className="text-gray-400 hover:text-primary focus:outline-none">✕</button>
                         </div>
                         <span className="block mb-2">A <b>pending</b> or <b>contingent</b> home is one that is under contract but not yet sold. "Contingent" means the sale depends on certain conditions being met (like inspection or financing). "Pending" means all contingencies have been met and the sale is close to completion.</span>
                         <span className="block">This filter hides homes that are not currently available for purchase because they are already under contract.</span>
                       </div>
                     )}
                   </div>
                 )}
                 {/* Max HOA fees per month */}
                 {activeFilterTab !== 'rent' && (
                   <div className="mb-2 relative">
                     <div className="flex items-center gap-1 mb-2">
                       <span className="text-lg font-medium text-foreground">Max HOA fees per month</span>
                       <button
                         type="button"
                         className="ml-1 text-gray-400 hover:text-primary focus:outline-none"
                         onClick={() => setInfoModal('hoa')}
                         aria-label="Info about HOA fees"
                       >
                         <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="1"/></svg>
                       </button>
                     </div>
                     <div className="flex gap-2">
                       <button
                         className={`w-16 h-10 rounded-full px-0 py-0 text-base font-bold border transition-colors flex items-center justify-center ${hoaMode === 'any' ? 'bg-primary text-white border-primary' : 'bg-background border-gray-400 text-gray-900 hover:border-gray-500'}`}
                         onClick={() => { setNoHoa(false); setMaxHoaFees(''); }}
                         type="button"
                         aria-label="Show all properties regardless of HOA fees"
                       >
                         Any
                       </button>
                       <button
                         className={`w-24 h-10 rounded-full px-0 py-0 text-base font-bold border transition-colors flex items-center justify-center ${hoaMode === 'noHoa' ? 'bg-primary text-white border-primary' : 'bg-background border-gray-400 text-gray-900 hover:border-gray-500'}`}
                         onClick={() => { setNoHoa(true); setMaxHoaFees(''); }}
                         type="button"
                         aria-label="Show only properties with no HOA fees"
                       >
                         No HOA
                       </button>
                       <div className={`w-44 h-10 relative flex items-center rounded-full border px-0 py-0 transition-colors flex-shrink-0 border-gray-400 bg-background focus-within:border-primary`}
                         onClick={() => { setHoaMode('max'); setNoHoa(false); }}
                         style={{ cursor: 'text' }}
                       >
                         <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                         <input
                           type="text"
                           inputMode="numeric"
                           pattern="[0-9,]*"
                           className="pl-7 pr-2 w-full h-full bg-transparent outline-none border-none text-base font-bold text-foreground placeholder:text-gray-400 rounded-full text-left"
                           placeholder="Max monthly fees"
                           value={formatNumberWithCommas(maxHoaFees)}
                           onFocus={() => { setHoaMode('max'); setNoHoa(false); }}
                           onChange={e => {
                             const val = e.target.value.replace(/[^0-9]/g, '');
                             setMaxHoaFees(val);
                           }}
                           min={0}
                           aria-label="Maximum HOA fees per month"
                         />
                       </div>
                     </div>
                     {/* Info Modal for HOA */}
                     {infoModal === 'hoa' && (
                       <div className="absolute left-1/2 top-full mt-2 -translate-x-1/2 z-50 bg-background text-foreground border border-border rounded-xl shadow-lg p-4 w-64 text-sm">
                         <div className="flex justify-between items-center mb-2">
                           <span className="font-bold">HOA Fees</span>
                           <button onClick={() => setInfoModal(null)} className="text-gray-400 hover:text-primary focus:outline-none" aria-label="Close HOA info modal">✕</button>
                         </div>
                         <span className="block mb-2">HOA (Homeowners Association) fees are monthly payments that cover community amenities and maintenance. These fees can vary widely and are typically higher in condos and planned communities.</span>
                         <span className="block">This filter helps you find properties within your HOA budget or properties with no HOA fees at all.</span>
                       </div>
                     )}
                   </div>
                 )}
               </div>
               {/* Remaining sections (copied exactly) */}
               {/* New construction only */}
               {activeFilterTab !== 'rent' && (
                 <div className="flex items-center justify-between">
                   <span className="text-lg font-medium text-foreground">New construction only</span>
                   <button className="flex items-center justify-between py-3" onClick={() => toggleFilterOption('newConstructionOnly')}>
                     <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${filters.newConstructionOnly ? 'border-primary bg-primary' : 'border-gray-300 bg-white'}`}>
                       {filters.newConstructionOnly && (
                         <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg>
                       )}
                     </span>
                   </button>
                 </div>
               )}
               {/* Days on market */}
               {activeFilterTab !== 'rent' && (
                 <div className="flex items-center justify-between mb-1">
                   <span className="text-lg font-medium text-foreground">Days on market</span>
                   <button 
                     className="bg-primary text-white rounded-full px-5 py-2 font-bold text-base shadow hover:bg-blue-700 transition-colors"
                     onClick={() => {
                       setPendingDaysOnMarket(filters.daysOnMarket ?? null);
                       setShowDaysOnMarketModal(true);
                     }}
                   >
                     {filters.daysOnMarket === 7
                       ? '7 Days'
                       : filters.daysOnMarket === 14
                         ? '14 Days'
                       : filters.daysOnMarket === 21
                         ? '21 Days'
                       : filters.daysOnMarket === 30
                         ? '30 Days'
                       : filters.daysOnMarket === 90
                         ? '90 Days'
                       : filters.daysOnMarket === 180
                         ? '6 Months'
                       : filters.daysOnMarket === 365
                         ? '12 Months+'
                       : filters.daysOnMarket
                         ? `${filters.daysOnMarket} days`
                         : 'Choose'}
                   </button>
                 </div>
               )}
               {/* End of modal content */}
            </div>
            // End exact copy
          )}
        </div>
        {/* Sticky footer action buttons */}
        <div className="px-4 py-4 border-t border-border bg-background text-foreground sticky bottom-0 z-10">
          <Button
            className="w-full rounded-full bg-primary text-primary-foreground font-bold py-3 text-lg"
            onClick={() => {
              // Close all secondary modals when the filters modal is closed
              setShowPropertyTypeModal(false);
              setShowSquareFeetModal(false);
              setShowDaysOnMarketModal(false);
              setShowSearchAreaDropdown(false);
              setShowLotSizeDropdown(false);
              setShowHomeAgeDropdown(false);
              setShowSquareFeetDropdown(false);
              setShowFilters(false);
            }}
          >
            {isSearching
              ? 'Searching...'
              : getFilteredListingsCount() === 0
                ? 'Adjust filters'
                : `See ${getFilteredListingsCount().toLocaleString()} homes`}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}; 